import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { cloneInitialAds, createInitialSelectedByCampaign } from './ads'
import {
  AUTOPILOT_NOW,
  advanceAutopilotClock,
  cloneInitialAutopilot,
  cloneInitialOptimizations,
  isAutopilotEligible,
  planCheck,
} from './autopilot'
import { cloneInitialRisks, cloneInitialViolations } from './compliance'
import { cloneInitialCreatives, pickProductAsset } from './creatives'
import campaignsJson from './fixtures/campaigns.json'
import enumsJson from './fixtures/enums.json'
import metricsW5Json from './fixtures/w5/metrics.json'
import reportW5Json from './fixtures/w5/report.json'
import rulesW5Json from './fixtures/w5/rules.json'
import type {
  Ad,
  Campaign,
  CampaignDraft,
  CampaignSettingsPatch,
  DemoDataStateV3,
  DemoStoreV3,
  LibraryCreative,
  LibraryUploadInput,
  Market,
  OptimizationDraft,
  OptimizationPayload,
  Product,
  VariantState,
} from './types'

export const DEMO_STORAGE_KEY = 'lfy-ad-demo-v1'

interface CampaignFixture {
  meta: { today: string; syncedAt: string }
  products: Product[]
  campaigns: Campaign[]
}

interface EnumFixture {
  objective: Record<string, string>
  market: Record<string, string>
}

const campaignFixture = campaignsJson as unknown as CampaignFixture
const enumFixture = enumsJson as unknown as EnumFixture

export type W5ProposalState = 'pending' | 'approved' | 'rejected'
export type W5TriggerState = 'done' | 'pending' | 'skip' | 'block' | 'rejected' | 'undone'

export interface W5CustomRule {
  key: string
  name: string
  purpose: string
  cond: string
  action: string
  scope: string
  weekRuns: number
  needsConfirm: boolean
  locked: false
  on: boolean
  createdAt: string
}

export interface W5TriggerEntry {
  id: string
  at: string
  rule: string
  object: string
  what: string
  state: W5TriggerState
  undoneAt: string | null
  byUser?: boolean
  hit?: string
  basis?: string
  result?: string
}

export interface W5ReportExport {
  seq: number
  at: string
}

export interface W5State {
  monitorRules: Record<string, boolean>
  autoRules: Array<{ key: string; on: boolean }>
  customRules: W5CustomRule[]
  proposal: {
    state: W5ProposalState
    dailyMax: number | null
    fromDailyMax: number | null
    decidedAt: string | null
    rejectReason: string | null
  }
  triggerLog: W5TriggerEntry[]
  findings: Record<string, { owner: 'you' | 'ai'; handedAt: string | null }>
  reportSentAt: string | null
  reportExports: W5ReportExport[]
  reportSendCount: number
  nextTriggerSeq: number
}

export interface W5Slice {
  w5: W5State
}

export interface W5Actions {
  setMonitorRule: (key: string, on: boolean) => void
  setAutoRule: (key: string, on: boolean) => void
  addCustomRule: (rule: Omit<W5CustomRule, 'key' | 'createdAt' | 'weekRuns' | 'locked' | 'on'>) => string
  approveW5Proposal: (dailyMax: number) => void
  rejectW5Proposal: (reason: string) => void
  undoW5Proposal: (entryId: string) => void
  handW5FindingToAi: (findingId: string) => void
  setW5ReportSentAt: (value: string) => void
  appendReportExport: () => W5ReportExport
}

type DemoDataStateV5 = DemoDataStateV3 & W5Slice
type DemoStoreV5 = DemoStoreV3 & W5Slice & W5Actions

export type W8PartnershipTier = 'entry' | 'growth' | 'scale'
export type ChainCreateLinkState = 'idle' | 'loading' | 'recognized' | 'error'

export interface W8State {
  partnership: { tier: W8PartnershipTier; confirmedAt: string | null }
  renewal: { confirmedAt: string | null }
}

export interface W8Slice {
  w8: W8State
  chainCreateLink: { state: ChainCreateLinkState; url: string }
  chainBusy: boolean
}

export interface W8Actions {
  confirmPartnership: (tier: W8PartnershipTier) => void
  confirmRenewal: () => void
  setChainCreateLink: (state: ChainCreateLinkState, url?: string) => void
  setChainBusy: (busy: boolean) => void
}

export type DemoDataStateV6 = DemoDataStateV5 & W8Slice
export type DemoStoreV6 = DemoStoreV5 & W8Slice & W8Actions

interface W5MetricsFixture {
  rules: Array<{ key: string; on: boolean }>
}

interface W5RulesFixture {
  items: Array<{ key: string; on: boolean; locked: boolean }>
  log: W5TriggerEntry[]
}

interface W5ReportFixture {
  findings: Array<{ id: string; owner: 'you' | 'ai' }>
  lastSent: string
}

const metricsW5 = metricsW5Json as unknown as W5MetricsFixture
const rulesW5 = rulesW5Json as unknown as W5RulesFixture
const reportW5 = reportW5Json as unknown as W5ReportFixture
function copy<T>(value: T): T {
  return structuredClone(value)
}

export function stationNow(): string {
  const [date = '01-01', time = '00:00'] = AUTOPILOT_NOW.split(' ')
  const [month = '1', day = '1'] = date.split('-')
  return `${Number(month)}月${Number(day)}日 ${time}`
}

function createInitialW5State(): W5State {
  return {
    monitorRules: Object.fromEntries(metricsW5.rules.map((rule) => [rule.key, rule.on])),
    autoRules: rulesW5.items.filter((rule) => !rule.locked).map((rule) => ({ key: rule.key, on: rule.on })),
    customRules: [],
    proposal: { state: 'pending', dailyMax: null, fromDailyMax: null, decidedAt: null, rejectReason: null },
    triggerLog: copy(rulesW5.log),
    findings: Object.fromEntries(reportW5.findings.map((finding) => [finding.id, { owner: finding.owner, handedAt: null }])),
    reportSentAt: reportW5.lastSent.replace(/^上次发送\s*/, ''),
    reportExports: [],
    reportSendCount: 0,
    nextTriggerSeq: rulesW5.log.length + 1,
  }
}

export function createInitialDemoState(): DemoDataStateV6 {
  const selectedByCampaign = createInitialSelectedByCampaign()
  return {
    version: 4,
    campaigns: copy(campaignFixture.campaigns),
    variantByCampaign: { 'c-mug-us': { productAnalysis: 0, creativeStrategies: 0 } },
    nextCampaignSeq: 7,
    creatives: cloneInitialCreatives(),
    attachedByCampaign: copy(selectedByCampaign),
    selectedByCampaign,
    ads: cloneInitialAds(),
    violations: cloneInitialViolations(),
    risks: cloneInitialRisks(),
    nextCreativeSeq: 25,
    autopilotByCampaign: cloneInitialAutopilot(),
    optimizations: cloneInitialOptimizations(),
    nextOptSeq: 12,
    w5: createInitialW5State(),
    w8: {
      partnership: { tier: 'growth', confirmedAt: '9月3日 10:32' },
      renewal: { confirmedAt: null },
    },
    chainCreateLink: { state: 'idle', url: '' },
    chainBusy: false,
  }
}

function autoName(draft: Pick<CampaignDraft, 'objective' | 'markets'> & Partial<Pick<Campaign, 'productId' | 'adName'>>): string {
  const productId = draft.productId ?? 'p-mug'
  const product = campaignFixture.products.find((item) => item.id === productId)
  if (!product) return draft.adName ?? ''
  const market = draft.markets[0] ?? 'US'
  return `${product.name}｜${enumFixture.objective[draft.objective]}｜${enumFixture.market[market]}`
}

function updateCampaign(id: string, update: (campaign: Campaign) => Campaign) {
  return (campaigns: Campaign[]) => campaigns.map((campaign) => campaign.id === id ? update(campaign) : campaign)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? value as Record<string, unknown> : null
}

function arrayOr<T>(source: Record<string, unknown>, key: string, fallback: T[]): T[] {
  return Array.isArray(source[key]) ? copy(source[key] as T[]) : fallback
}

function recordOr<T>(source: Record<string, unknown>, key: string, fallback: Record<string, T>): Record<string, T> {
  const value = asRecord(source[key])
  return value ? copy(value as Record<string, T>) : fallback
}

function withCampaignKeys(
  campaigns: Campaign[],
  attachedByCampaign: Record<string, string[]>,
  selectedByCampaign: Record<string, string[]>,
): Pick<DemoDataStateV3, 'attachedByCampaign' | 'selectedByCampaign'> {
  const attached = copy(attachedByCampaign)
  const selected = copy(selectedByCampaign)
  for (const campaign of campaigns) {
    attached[campaign.id] ??= []
    selected[campaign.id] ??= []
  }
  return { attachedByCampaign: attached, selectedByCampaign: selected }
}

export function migrateDemoState(persisted: unknown, fromVersion: number): DemoDataStateV5 {
  const initial = createInitialDemoState()
  const source = asRecord(persisted)
  if (!source || ![1, 2, 3, 4].includes(fromVersion)) return initial
  const campaigns = arrayOr<Campaign>(source, 'campaigns', initial.campaigns)
  const attachedByCampaign = recordOr<string[]>(source, 'attachedByCampaign', initial.attachedByCampaign)
  const selectedByCampaign = recordOr<string[]>(source, 'selectedByCampaign', initial.selectedByCampaign)
  const shared: DemoDataStateV5 = {
    ...initial,
    campaigns,
    variantByCampaign: recordOr<VariantState>(source, 'variantByCampaign', initial.variantByCampaign),
    nextCampaignSeq: typeof source.nextCampaignSeq === 'number' ? source.nextCampaignSeq : initial.nextCampaignSeq,
    creatives: arrayOr<LibraryCreative>(source, 'creatives', initial.creatives),
    ads: arrayOr<Ad>(source, 'ads', initial.ads),
    violations: arrayOr(source, 'violations', initial.violations),
    risks: arrayOr(source, 'risks', initial.risks),
    nextCreativeSeq: typeof source.nextCreativeSeq === 'number' ? source.nextCreativeSeq : initial.nextCreativeSeq,
    autopilotByCampaign: recordOr(source, 'autopilotByCampaign', initial.autopilotByCampaign),
    optimizations: arrayOr(source, 'optimizations', initial.optimizations),
    nextOptSeq: typeof source.nextOptSeq === 'number' ? source.nextOptSeq : initial.nextOptSeq,
    w5: initial.w5,
    version: 4,
    ...withCampaignKeys(
      campaigns,
      fromVersion === 1 ? initial.attachedByCampaign : attachedByCampaign,
      fromVersion === 1 ? initial.selectedByCampaign : selectedByCampaign,
    ),
  }
  return shared
}

function nextLicenseDate(value: string | null): string | null {
  if (!value) return value
  const year = Number(value.slice(0, 4))
  return `${year + 1}${value.slice(4)}`
}

function unique(items: string[]): string[] {
  return [...new Set(items)]
}

export const useDemoStore = create<DemoStoreV6>()(
  persist(
    (set, get) => ({
      ...createInitialDemoState(),
      createCampaign: (draft) => {
        const id = `c-new-${get().nextCampaignSeq}`
        const adName = draft.adName?.trim() || autoName(draft)
        const campaign: Campaign = {
          id,
          productId: 'p-mug',
          adName,
          adNameEdited: Boolean(draft.adName?.trim()),
          objective: draft.objective,
          resultMetric: draft.objective === 'sales' ? 'orders' : 'reach',
          markets: copy(draft.markets),
          audience: copy(draft.audience),
          budget: copy(draft.budget),
          status: 'pending',
          attentionReason: null,
          spendMonth: 0,
          result: { current: 0, target: draft.objective === 'sales' ? 200 : 500000 },
          orders: 0,
          reach: 0,
          impressions: 0,
          revenue: 0,
          metrics: { cpa: null, roas: null, cpm: null },
          platform: 'Meta',
          adCount: 1,
          deltaPrev: null,
          createdAt: campaignFixture.meta.today,
          seeded: false,
        }
        const creativeId = get().creatives.find((creative) => creative.productId === campaign.productId)?.id ?? 'cr-01'
        const ad: Ad = {
          id: `ad-${id}`,
          campaignId: id,
          name: adName,
          creativeId,
          status: 'live',
          spend: 0,
          orders: 0,
          impressions: 0,
          clicks: 0,
          reach: 0,
        }
        set((state) => ({
          campaigns: [...state.campaigns, campaign],
          variantByCampaign: { ...state.variantByCampaign, [id]: { productAnalysis: 0, creativeStrategies: 0 } },
          attachedByCampaign: { ...state.attachedByCampaign, [id]: [creativeId] },
          selectedByCampaign: { ...state.selectedByCampaign, [id]: [creativeId] },
          ads: [...state.ads, ad],
          nextCampaignSeq: state.nextCampaignSeq + 1,
        }))
        return id
      },
      confirmCampaign: (id) => set((state) => ({ campaigns: updateCampaign(id, (campaign) => campaign.status === 'pending' ? { ...campaign, status: 'running', attentionReason: null } : campaign)(state.campaigns) })),
      pauseCampaign: (id) => set((state) => ({ campaigns: updateCampaign(id, (campaign) => campaign.status === 'running' ? { ...campaign, status: 'paused' } : campaign)(state.campaigns) })),
      resumeCampaign: (id) => set((state) => ({ campaigns: updateCampaign(id, (campaign) => campaign.status === 'paused' ? { ...campaign, status: 'running' } : campaign)(state.campaigns) })),
      fixProductLink: (id, url) => {
        void url
        set((state) => ({ campaigns: updateCampaign(id, (campaign) => campaign.status === 'attention' ? { ...campaign, status: 'running', attentionReason: null } : campaign)(state.campaigns) }))
      },
      updateSettings: (id, patch: CampaignSettingsPatch) => set((state) => ({
        campaigns: updateCampaign(id, (campaign) => {
          const next = { ...campaign, ...copy(patch) }
          if (patch.objective !== undefined) {
            next.resultMetric = next.objective === 'sales' ? 'orders' : 'reach'
            next.result = { ...next.result, target: next.objective === 'sales' ? 200 : 500000 }
          }
          if (!next.adNameEdited && (patch.objective !== undefined || patch.markets !== undefined)) next.adName = autoName(next)
          return next
        })(state.campaigns),
      })),
      uploadCreative: (input: LibraryUploadInput) => {
        const sequence = get().nextCreativeSeq
        const id = `cr-${String(sequence).padStart(2, '0')}`
        const creative: LibraryCreative = {
          id,
          name: input.name,
          kind: input.kind,
          duration: input.kind === 'video' ? '0:15' : null,
          source: 'upload',
          kol: null,
          licenseUntil: null,
          productId: input.productId,
          cover: '',
          clip: null,
          dataUrl: input.dataUrl,
          createdAt: campaignFixture.meta.today,
          seeded: false,
          compliance: null,
        }
        set((state) => ({ creatives: [...state.creatives, creative], nextCreativeSeq: state.nextCreativeSeq + 1 }))
        return id
      },
      cycleVariant: (campaignId, key) => set((state) => {
        const current: VariantState = state.variantByCampaign[campaignId] ?? { productAnalysis: 0, creativeStrategies: 0 }
        return { variantByCampaign: { ...state.variantByCampaign, [campaignId]: { ...current, [key]: current[key] + 1 } } }
      }),
      addAiCreatives: (productId, directions, count) => {
        const firstSequence = get().nextCreativeSeq
        const ids = Array.from({ length: count }, (_, index) => `cr-${String(firstSequence + index).padStart(2, '0')}`)
        const additions = ids.map<LibraryCreative>((id, index) => {
          const sequence = firstSequence + index
          const direction = directions[index % Math.max(directions.length, 1)] ?? '智能延展'
          const kind = index % 3 === 2 ? 'image' : 'video'
          const asset = pickProductAsset(productId, kind, index)
          return {
            id,
            name: `AI 素材 ${String(sequence).padStart(2, '0')} · ${direction}`,
            kind,
            duration: kind === 'video' ? '0:15' : null,
            source: 'ai',
            kol: null,
            licenseUntil: null,
            productId,
            cover: asset.cover,
            clip: asset.clip,
            createdAt: campaignFixture.meta.today,
            seeded: false,
            compliance: null,
          }
        })
        set((state) => ({ creatives: [...state.creatives, ...additions], nextCreativeSeq: state.nextCreativeSeq + count }))
        return ids
      },
      renameCreative: (id, name) => set((state) => ({
        creatives: state.creatives.map((creative) => creative.id === id ? { ...creative, name } : creative),
      })),
      deleteCreative: (id) => {
        const state = get()
        const creative = state.creatives.find((item) => item.id === id)
        if (!creative) return { ok: false, reason: '没有找到这条素材' }
        if (creative.source === 'kol') return { ok: false, reason: '达人授权素材由平台方提供，不能删除' }
        if (state.ads.some((ad) => ad.creativeId === id)) return { ok: false, reason: '使用中的素材不能删除' }
        set((current) => ({
          creatives: current.creatives.filter((item) => item.id !== id),
          attachedByCampaign: Object.fromEntries(Object.entries(current.attachedByCampaign).map(([campaignId, ids]) => [campaignId, ids.filter((item) => item !== id)])),
          selectedByCampaign: Object.fromEntries(Object.entries(current.selectedByCampaign).map(([campaignId, ids]) => [campaignId, ids.filter((item) => item !== id)])),
        }))
        return { ok: true }
      },
      attachCreatives: (campaignId, ids) => set((state) => {
        const productId = state.campaigns.find((campaign) => campaign.id === campaignId)?.productId
        const allowed = state.creatives.filter((creative) => ids.includes(creative.id) && creative.productId === productId).map((creative) => creative.id)
        return { attachedByCampaign: { ...state.attachedByCampaign, [campaignId]: unique([...(state.attachedByCampaign[campaignId] ?? []), ...allowed]) } }
      }),
      detachCreatives: (campaignId, ids) => set((state) => {
        const selected = new Set(state.selectedByCampaign[campaignId] ?? [])
        const removable = new Set(ids.filter((id) => !selected.has(id)))
        return {
          attachedByCampaign: {
            ...state.attachedByCampaign,
            [campaignId]: (state.attachedByCampaign[campaignId] ?? []).filter((id) => !removable.has(id)),
          },
        }
      }),
      toggleSelected: (campaignId, creativeId) => {
        const state = get()
        const current = state.selectedByCampaign[campaignId] ?? []
        if (current.includes(creativeId) && current.length === 1) return { ok: false, reason: '至少要选用一条素材' }
        const campaign = state.campaigns.find((item) => item.id === campaignId)
        const creative = state.creatives.find((item) => item.id === creativeId)
        if (!campaign || !creative || campaign.productId !== creative.productId) return { ok: false, reason: '素材与广告任务商品不一致' }
        const next = current.includes(creativeId) ? current.filter((id) => id !== creativeId) : [...current, creativeId]
        set((latest) => ({
          selectedByCampaign: { ...latest.selectedByCampaign, [campaignId]: next },
          attachedByCampaign: { ...latest.attachedByCampaign, [campaignId]: unique([...(latest.attachedByCampaign[campaignId] ?? []), creativeId]) },
        }))
        return { ok: true }
      },
      replaceAdCreative: (adId, creativeId) => set((state) => {
        const ad = state.ads.find((item) => item.id === adId)
        const campaign = ad ? state.campaigns.find((item) => item.id === ad.campaignId) : undefined
        const creative = state.creatives.find((item) => item.id === creativeId)
        if (!ad || !campaign || !creative || creative.productId !== campaign.productId) return state
        return {
          ads: state.ads.map((item) => item.id === adId ? { ...item, creativeId } : item),
          selectedByCampaign: {
            ...state.selectedByCampaign,
            [campaign.id]: (state.selectedByCampaign[campaign.id] ?? []).map((id) => id === ad.creativeId ? creativeId : id),
          },
          attachedByCampaign: {
            ...state.attachedByCampaign,
            [campaign.id]: unique([...(state.attachedByCampaign[campaign.id] ?? []), creativeId]),
          },
          violations: state.violations.map((violation) => violation.adId === adId && violation.state !== 'resolved' ? { ...violation, state: 'reviewing' } : violation),
        }
      }),
      resolveViolation: (id) => set((state) => {
        const violation = state.violations.find((item) => item.id === id)
        return {
          violations: state.violations.map((item) => item.id === id ? { ...item, state: 'resolved' } : item),
          ads: violation ? state.ads.map((ad) => ad.id === violation.adId ? { ...ad, status: 'live' } : ad) : state.ads,
        }
      }),
      appealViolation: (id) => set((state) => ({
        violations: state.violations.map((violation) => violation.id === id && violation.state === 'open' ? { ...violation, state: 'appealing' } : violation),
      })),
      resolveRisk: (id) => set((state) => ({
        risks: state.risks.map((risk) => risk.id === id && risk.state === 'open' ? { ...risk, state: 'resolved' } : risk),
      })),
      ignoreRisk: (id) => set((state) => ({
        risks: state.risks.map((risk) => risk.id === id && risk.state === 'open' ? { ...risk, state: 'ignored' } : risk),
      })),
      renewLicense: (creativeId) => set((state) => {
        const campaignIds = new Set(state.ads.filter((ad) => ad.creativeId === creativeId).map((ad) => ad.campaignId))
        return {
          creatives: state.creatives.map((creative) => creative.id === creativeId ? { ...creative, licenseUntil: nextLicenseDate(creative.licenseUntil) } : creative),
          risks: state.risks.map((risk) => risk.state === 'open' && risk.type === 'license' && campaignIds.has(risk.campaignId) ? { ...risk, state: 'resolved' } : risk),
        }
      }),
      turnOnAutopilot: (campaignId, input) => set((state) => {
        const campaign = state.campaigns.find((item) => item.id === campaignId)
        if (!isAutopilotEligible(campaign).ok || !Number.isFinite(input.target) || input.target <= 0) return state
        return {
          autopilotByCampaign: {
            ...state.autopilotByCampaign,
            [campaignId]: {
              on: true,
              goal: input.goal,
              target: input.target,
              startedAt: AUTOPILOT_NOW,
              lastCheckAt: state.autopilotByCampaign[campaignId]?.lastCheckAt ?? AUTOPILOT_NOW,
              turnedOffAt: null,
            },
          },
        }
      }),
      turnOffAutopilot: (campaignId) => set((state) => {
        const current = state.autopilotByCampaign[campaignId]
        if (!current?.on) return state
        return {
          autopilotByCampaign: {
            ...state.autopilotByCampaign,
            [campaignId]: { ...current, on: false, turnedOffAt: AUTOPILOT_NOW },
          },
        }
      }),
      updateAutopilotGoal: (campaignId, input) => set((state) => {
        const current = state.autopilotByCampaign[campaignId]
        if (!current || !Number.isFinite(input.target) || input.target <= 0) return state
        return {
          autopilotByCampaign: {
            ...state.autopilotByCampaign,
            [campaignId]: { ...current, goal: input.goal, target: input.target },
          },
        }
      }),
      appendOptimization: (draft: OptimizationDraft) => {
        const sequence = get().nextOptSeq
        const id = `op-${String(sequence).padStart(2, '0')}`
        set((state) => ({
          optimizations: [...state.optimizations, {
            id,
            campaignId: draft.campaignId,
            at: AUTOPILOT_NOW,
            type: draft.type,
            icon: draft.icon,
            action: draft.action,
            why: draft.why,
            target: draft.target,
            targetKind: draft.targetKind,
            undone: false,
            undoneAt: null,
            payload: draft.payload ?? null,
            seeded: false,
            reasonKey: draft.reasonKey,
          }],
          nextOptSeq: state.nextOptSeq + 1,
        }))
        return id
      },
      undoOptimization: (id) => set((state) => {
        const optimization = state.optimizations.find((item) => item.id === id)
        if (!optimization || optimization.undone || !optimization.payload) return state
        const payload = optimization.payload
        const common = {
          optimizations: state.optimizations.map((item) => item.id === id ? { ...item, undone: true, undoneAt: AUTOPILOT_NOW } : item),
        }
        if (payload.kind === 'variant') {
          const current = state.variantByCampaign[optimization.campaignId] ?? { productAnalysis: 0, creativeStrategies: 0 }
          return {
            ...common,
            variantByCampaign: {
              ...state.variantByCampaign,
              [optimization.campaignId]: { ...current, [payload.key]: payload.from },
            },
          }
        }
        if (payload.kind === 'audience') {
          return {
            ...common,
            campaigns: updateCampaign(optimization.campaignId, (campaign) => ({ ...campaign, audience: copy(payload.from) }))(state.campaigns),
          }
        }
        if (payload.kind === 'creative') {
          return {
            ...common,
            ads: state.ads.map((ad) => ad.id === payload.adId ? {
              ...ad,
              creativeId: payload.fromCreativeId,
              status: payload.violationId ? 'rejected' : ad.status,
            } : ad),
            selectedByCampaign: {
              ...state.selectedByCampaign,
              [optimization.campaignId]: (state.selectedByCampaign[optimization.campaignId] ?? []).map((creativeId) => creativeId === payload.toCreativeId ? payload.fromCreativeId : creativeId),
            },
            violations: state.violations.map((violation) => violation.id === payload.violationId ? { ...violation, state: 'open' } : violation),
          }
        }
        return {
          ...common,
          risks: state.risks.map((risk) => risk.id === payload.riskId ? { ...risk, state: payload.from } : risk),
        }
      }),
      runCheck: (campaignId) => {
        const result = planCheck(get(), campaignId)
        for (const draft of result.drafts) {
          let payload: OptimizationPayload | null = null
          let executedDraft = draft
          if (draft.effect.kind === 'variant') {
            const current = get().variantByCampaign[campaignId] ?? { productAnalysis: 0, creativeStrategies: 0 }
            payload = { kind: 'variant', key: draft.effect.key, from: current[draft.effect.key] }
            get().cycleVariant(campaignId, draft.effect.key)
          } else if (draft.effect.kind === 'audience') {
            const current = get().campaigns.find((campaign) => campaign.id === campaignId)
            if (current) {
              payload = { kind: 'audience', from: copy(current.audience) }
              get().updateSettings(campaignId, { audience: copy(draft.effect.audience) })
            }
          } else if (draft.effect.kind === 'creative') {
            const effect = draft.effect
            const before = get().ads.find((ad) => ad.id === effect.adId)
            let creativeId = effect.creativeId
            if (!creativeId) {
              creativeId = get().addAiCreatives(
                get().campaigns.find((campaign) => campaign.id === campaignId)?.productId ?? '',
                ['自动优化'],
                1,
              )[0] ?? null
              const generated = get().creatives.find((creative) => creative.id === creativeId)
              if (generated) executedDraft = { ...draft, action: `换上了素材「${generated.name}」` }
            }
            if (before && creativeId) {
              payload = {
                kind: 'creative',
                adId: before.id,
                fromCreativeId: before.creativeId,
                toCreativeId: creativeId,
                violationId: effect.violationId,
              }
              get().replaceAdCreative(before.id, creativeId)
              if (effect.violationId) get().resolveViolation(effect.violationId)
            }
          } else if (draft.effect.kind === 'risk') {
            const effect = draft.effect
            const current = get().risks.find((risk) => risk.id === effect.riskId)
            if (current) {
              payload = { kind: 'risk', riskId: current.id, from: current.state }
              get().resolveRisk(current.id)
            }
          }
          get().appendOptimization({ ...executedDraft, payload })
        }
        return { count: result.drafts.length, reason: result.reason }
      },
      runCheckAll: () => {
        const campaignIds = Object.entries(get().autopilotByCampaign)
          .filter(([, autopilot]) => autopilot.on)
          .map(([campaignId]) => campaignId)
        return campaignIds.reduce((total, campaignId) => total + get().runCheck(campaignId).count, 0)
      },
      setMonitorRule: (key, on) => set((state) => ({
        w5: { ...state.w5, monitorRules: { ...state.w5.monitorRules, [key]: on } },
      })),
      setAutoRule: (key, on) => set((state) => ({
        w5: {
          ...state.w5,
          autoRules: state.w5.autoRules.map((rule) => rule.key === key ? { ...rule, on } : rule),
          customRules: state.w5.customRules.map((rule) => rule.key === key ? { ...rule, on } : rule),
        },
      })),
      addCustomRule: (rule) => {
        advanceAutopilotClock()
        const key = `R${8 + get().w5.customRules.length}`
        set((state) => ({
          w5: {
            ...state.w5,
            customRules: [...state.w5.customRules, {
              ...rule,
              key,
              weekRuns: 0,
              locked: false,
              on: true,
              createdAt: stationNow(),
            }],
          },
        }))
        return key
      },
      approveW5Proposal: (dailyMax) => {
        const current = get()
        const campaign = current.campaigns.find((item) => item.id === 'c-mug-us')
        if (!campaign) return
        const fromDailyMax = campaign.budget.dailyMax
        if (dailyMax < fromDailyMax || dailyMax > Math.round(fromDailyMax * 1.2)) return
        advanceAutopilotClock()
        set((state) => {
          const now = stationNow()
          const nextTriggerSeq = state.w5.nextTriggerSeq ?? state.w5.triggerLog.length + 1
          const format = (value: number) => `$${value.toLocaleString('en-US')}`
          const dailyUsed = Math.round(campaign.spendMonth / 7 / fromDailyMax * 100)
          return {
            campaigns: updateCampaign('c-mug-us', (item) => ({
              ...item,
              budget: { ...item.budget, dailyMax },
            }))(state.campaigns),
            w5: {
              ...state.w5,
              proposal: { state: 'approved', dailyMax, fromDailyMax, decidedAt: now, rejectReason: null },
              nextTriggerSeq: nextTriggerSeq + 1,
              triggerLog: [
                ...state.w5.triggerLog.map((entry) => entry.id === 't-1' ? { ...entry, state: 'done' as const } : entry),
                {
                  id: `t-${nextTriggerSeq}`,
                  at: now,
                  rule: 'R2 放量',
                  object: '轻量保温杯',
                  what: `日预算上限 ${format(fromDailyMax)} → ${format(dailyMax)}（你批准）`,
                  state: 'done',
                  undoneAt: null,
                  byUser: true,
                  hit: `广告回报 ${(campaign.metrics.roas ?? 0).toFixed(1)}；阈值 4.0 · 日预算消耗 ${dailyUsed}%；阈值 80%`,
                  basis: '单日预算变动不超过 20%，预算类动作需人工确认',
                  result: `已执行，日预算上限已更新为 ${format(dailyMax)}`,
                },
              ],
            },
          }
        })
      },
      rejectW5Proposal: (reason) => {
        const current = get()
        const campaign = current.campaigns.find((item) => item.id === 'c-mug-us')
        if (!campaign) return
        const fromDailyMax = campaign.budget.dailyMax
        advanceAutopilotClock()
        set((state) => {
          const now = stationNow()
          const nextTriggerSeq = state.w5.nextTriggerSeq ?? state.w5.triggerLog.length + 1
          const format = (value: number) => `$${value.toLocaleString('en-US')}`
          const proposed = Math.round(fromDailyMax * 1.2)
          const dailyUsed = Math.round(campaign.spendMonth / 7 / fromDailyMax * 100)
          return {
            w5: {
              ...state.w5,
              proposal: { state: 'rejected', dailyMax: null, fromDailyMax, decidedAt: now, rejectReason: reason },
              nextTriggerSeq: nextTriggerSeq + 1,
              triggerLog: [
                ...state.w5.triggerLog.map((entry) => entry.id === 't-1' ? { ...entry, state: 'rejected' as const } : entry),
                {
                  id: `t-${nextTriggerSeq}`,
                  at: now,
                  rule: 'R2 放量',
                  object: '轻量保温杯',
                  what: `日预算上限 ${format(fromDailyMax)} → ${format(proposed)}（你拒绝：${reason}）`,
                  state: 'rejected',
                  undoneAt: null,
                  byUser: true,
                  hit: `广告回报 ${(campaign.metrics.roas ?? 0).toFixed(1)}；阈值 4.0 · 日预算消耗 ${dailyUsed}%；阈值 80%`,
                  basis: '单日预算变动不超过 20%，预算类动作需人工确认',
                  result: `你已拒绝，日预算上限保持 ${format(fromDailyMax)}`,
                },
              ],
            },
          }
        })
      },
      undoW5Proposal: (entryId) => {
        const current = get()
        const entry = current.w5.triggerLog.find((item) => item.id === entryId)
        const campaign = current.campaigns.find((item) => item.id === 'c-mug-us')
        if (!entry?.byUser || !campaign) return
        const fromDailyMax = current.w5.proposal.fromDailyMax ?? campaign.budget.dailyMax
        advanceAutopilotClock()
        set((state) => {
          const undoneAt = stationNow().split(' ').at(-1) ?? ''
          return {
            campaigns: updateCampaign('c-mug-us', (item) => ({
              ...item,
              budget: { ...item.budget, dailyMax: fromDailyMax },
            }))(state.campaigns),
            w5: {
              ...state.w5,
              proposal: { state: 'pending', dailyMax: null, fromDailyMax: null, decidedAt: null, rejectReason: null },
              triggerLog: state.w5.triggerLog.map((item) => {
                if (item.id === 't-1') return { ...item, state: 'pending' }
                if (item.id === entryId && item.byUser) return { ...item, state: 'undone', undoneAt }
                return item
              }),
            },
          }
        })
      },
      handW5FindingToAi: (findingId) => {
        advanceAutopilotClock()
        set((state) => ({
          w5: {
            ...state.w5,
            findings: {
              ...state.w5.findings,
              [findingId]: { owner: 'ai', handedAt: stationNow() },
            },
          },
        }))
      },
      setW5ReportSentAt: () => {
        advanceAutopilotClock()
        set((state) => ({
          w5: {
            ...state.w5,
            reportSentAt: stationNow(),
            reportSendCount: (state.w5.reportSendCount ?? 0) + 1,
          },
        }))
      },
      appendReportExport: () => {
        advanceAutopilotClock()
        const reportExports = get().w5.reportExports ?? []
        const item = { seq: reportExports.length + 1, at: stationNow() }
        set((state) => ({ w5: { ...state.w5, reportExports: [...(state.w5.reportExports ?? []), item] } }))
        return item
      },
      confirmPartnership: (tier) => set((state) => ({
        w8: {
          partnership: { tier, confirmedAt: stationNow() },
          renewal: state.w8?.renewal ?? { confirmedAt: null },
        },
      })),
      confirmRenewal: () => set((state) => ({
        w8: {
          partnership: state.w8?.partnership ?? { tier: 'growth', confirmedAt: '9月3日 10:32' },
          renewal: { confirmedAt: stationNow() },
        },
      })),
      setChainCreateLink: (linkState, url = '') => set({ chainCreateLink: { state: linkState, url } }),
      setChainBusy: (busy) => set({ chainBusy: busy }),
      reset: () => {
        if (typeof window !== 'undefined') window.localStorage.removeItem(DEMO_STORAGE_KEY)
        set(createInitialDemoState())
      },
    }),
    {
      name: DEMO_STORAGE_KEY,
      version: 7,
      migrate: migrateDemoState,
      partialize: (state) => ({
        version: state.version,
        campaigns: state.campaigns,
        variantByCampaign: state.variantByCampaign,
        nextCampaignSeq: state.nextCampaignSeq,
        creatives: state.creatives,
        attachedByCampaign: state.attachedByCampaign,
        selectedByCampaign: state.selectedByCampaign,
        ads: state.ads,
        violations: state.violations,
        risks: state.risks,
        nextCreativeSeq: state.nextCreativeSeq,
        autopilotByCampaign: state.autopilotByCampaign,
        optimizations: state.optimizations,
        nextOptSeq: state.nextOptSeq,
        w5: state.w5,
        w8: state.w8,
      }),
    },
  ),
)

export const demoProducts = copy(campaignFixture.products)

export function marketLabel(market: Market): string {
  return enumFixture.market[market] ?? market
}
